import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { onBootProgress } from '../lib/loader';
import { EASE } from '../lib/motion';

const MIN_DURATION_MS = 1600;

/**
 * Full-screen boot screen. The counter tracks real readiness (fonts, both
 * WebGL scenes compiled, portrait sampled), paced by a minimum duration so the
 * reveal has rhythm. When it hits 100 the counter slides out and a two-layer
 * curtain lifts to uncover the first frame of the 3D scene.
 */
export function Loader({ onReveal }: { onReveal: () => void }) {
  const reduce = useReducedMotion();
  const counterRef = useRef<HTMLSpanElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'loading' | 'exit' | 'gone'>('loading');

  useEffect(() => {
    let target = 0;
    let shown = 0;
    let raf = 0;
    const start = performance.now();
    const minMs = reduce ? 0 : MIN_DURATION_MS;
    const off = onBootProgress((p) => (target = p));

    const tick = () => {
      const cap = minMs ? Math.min(1, (performance.now() - start) / minMs) : 1;
      shown += (Math.min(target, cap) - shown) * 0.08;
      if (Math.min(target, cap) >= 1 && shown > 0.995) shown = 1;
      const value = Math.round(shown * 100);
      if (counterRef.current) counterRef.current.textContent = String(value);
      if (barRef.current) barRef.current.style.transform = `scaleX(${shown})`;
      if (shown >= 1) {
        setPhase('exit');
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      off();
    };
  }, [reduce]);

  useEffect(() => {
    if (phase !== 'exit') return;
    // Let the counter clear first, then hand over while the curtain rises.
    const t = window.setTimeout(onReveal, reduce ? 0 : 520);
    return () => window.clearTimeout(t);
  }, [phase, onReveal, reduce]);

  if (phase === 'gone') return null;
  const exiting = phase === 'exit';
  const curtain = { duration: reduce ? 0.3 : 1.15, ease: EASE };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={!exiting}
      className="pointer-events-none fixed inset-0 z-[80]"
    >
      <span className="sr-only">{exiting ? 'Loaded' : 'Loading'}</span>

      {/* Accent under-layer trails the main curtain for a layered wipe. */}
      <motion.div
        aria-hidden
        className="absolute inset-0 bg-accent-deep"
        initial={{ clipPath: 'inset(0% 0% 0% 0%)' }}
        animate={exiting ? { clipPath: 'inset(0% 0% 100% 0%)' } : undefined}
        transition={{ ...curtain, delay: reduce ? 0 : 0.62 }}
        onAnimationComplete={() => exiting && setPhase('gone')}
      />

      <motion.div
        className="pointer-events-auto absolute inset-0 flex flex-col justify-between bg-base px-5 py-6 md:px-8 md:py-8"
        initial={{ clipPath: 'inset(0% 0% 0% 0%)' }}
        animate={exiting ? { clipPath: 'inset(0% 0% 100% 0%)' } : undefined}
        transition={{ ...curtain, delay: reduce ? 0 : 0.42 }}
      >
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between">
          <span className="font-display text-xl font-semibold tracking-tight">
            kouk<span className="text-accent">.gr</span>
          </span>
        </div>

        <div className="mx-auto w-full max-w-[1400px]">
          <div className="overflow-hidden pb-[0.06em]">
            <motion.div
              className="flex items-start font-mono font-medium leading-[0.82] tracking-[-0.06em] text-fg tabular-nums"
              animate={exiting && !reduce ? { y: '-105%' } : undefined}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <span ref={counterRef} className="text-[clamp(6rem,22vw,20rem)]">
                0
              </span>
              <span className="mt-[0.35em] text-[clamp(1.5rem,4vw,3.5rem)] text-accent">
                %
              </span>
            </motion.div>
          </div>
          <div className="mt-6 h-px w-full bg-line">
            <div
              ref={barRef}
              className="h-full origin-left bg-accent"
              style={{ transform: 'scaleX(0)' }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
